import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, googleLoginUser, clearError } from '../../store/slices/authSlice';
import { loadGoogleScript, parseJwt } from '../../utils/googleAuth';
import AuthLayout from './AuthLayout';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { loading, error, isAuthenticated } = useSelector((state) => state.auth);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    const query = new URLSearchParams(location.search);
    const redirectParam = query.get('redirect');
    const from = redirectParam || location.state?.from || '/';

    useEffect(() => {
        if (isAuthenticated) {
            navigate(from, { replace: true });
        }
        return () => {
            dispatch(clearError());
        };
    }, [isAuthenticated, navigate, dispatch, from]);

    const handleGoogleLoginResponse = async (response) => {
        if (response.credential) {
            const payload = parseJwt(response.credential);
            if (payload) {
                dispatch(googleLoginUser({
                    displayName: payload.name || '',
                    email: payload.email || '',
                    id: payload.sub || '',
                    photoUrl: payload.picture || '',
                    user_type: 'student', // Default to student on login
                }));
            }
        }
    };

    useEffect(() => {
        loadGoogleScript(() => {
            if (window.google?.accounts?.id) {
                window.google.accounts.id.initialize({
                    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                    callback: handleGoogleLoginResponse,
                });
                window.google.accounts.id.renderButton(
                    document.getElementById('google-signin-btn'),
                    { theme: 'outline', size: 'large', type: 'standard', width: 320 }
                );
            }
        });
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        dispatch(loginUser({ email, password }));
    };

    const isFormValid = email.length > 0 && password.length >= 6; // API might allow 6? Placeholder said 8. I'll stick to 6 for flexibility if API allows.

    return (
        <AuthLayout
            title="Welcome Back"
            subtitle="Login to access your account"
        >
            {error && <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>{error}</Alert>}
            
            <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="loginEmail">
                    <Form.Label>Email / Username</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                    />
                </Form.Group>
 
                <Form.Group className="mb-3" controlId="loginPassword">
                    <Form.Label>Password</Form.Label>
                    <div className="password-icon-wrapper">
                        <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter password"
                            value={password}
                            maxLength={128}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={loading}
                        />
                        <span className="eye-btn" onClick={() => setShowPassword(!showPassword)} >
                            {showPassword ? <i className="bi bi-eye-slash"></i> : <i className="bi bi-eye"></i>}
                        </span>
                    </div>
                </Form.Group>

                <Form.Group className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4" controlId="rememberMe">
                    <Form.Check
                        type="checkbox"
                        label="Remember me"
                        className="text-muted"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        disabled={loading}
                    />
                    <Link to="/forgot-password" disabled={loading} className="text-primary text-decoration-none small fw-medium">
                        Forgot Password?
                    </Link>
                </Form.Group>

                <Button
                    variant="primary"
                    type="submit"
                    className="w-100 btn-custom d-flex align-items-center justify-content-center gap-2"
                    disabled={!isFormValid || loading}
                >
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" />
                            Signing In...
                        </>
                    ) : 'Sign In'}
                </Button>

                <div className="text-center my-3 text-muted text-sm">
                    <span>or sign in with</span>
                </div>

                <div className="d-flex justify-content-center mb-3">
                    <div id="google-signin-btn"></div>
                </div>

                <div className="auth-footer">
                    Don't have an account? <Link to="/signup">Sign Up</Link>
                </div>
            </Form>
        </AuthLayout>
    );
};

export default Login;
